import { createRoot } from 'react-dom/client';
import { registerHighlighter } from '@osd/ui/code-block';
import { App } from './App';
import { highlightToHtml } from './lib/highlight';
import '@osd/ui/styles.css';
// Web-local styles: AppShell chrome + the LoginForm composite
// styles. Both live in `apps/web/src/styles/` because they're
// app-specific (not part of the @osd/ui design-system surface).
import './styles/app-shell.scss';
import './styles/_login.scss';
// Font Awesome was removed 2026-09-22 (root/osd#123 — replace FA
// with @osd/icons). All icons in the chrome + auth pages now come
// from `@osd/icons` (per-icon subpath imports, tree-shaken into
// the production build). See
// https://gitlab.local:8929/root/osd/-/issues/123.

// Wire the Shiki-backed highlighter into @osd/ui/code-block. The
// design system exposes a pluggable highlighter (no hard dep on
// Shiki) — apps register their own at boot. This is the ONLY place
// in the app that knows about Shiki; everywhere else just calls
// <CodeBlock language="tsx">…</CodeBlock>.
// (root/osd#117 — promoted CodeBlock from web-local to @osd/ui).
registerHighlighter(async (code, lang) => {
  // Cast: @osd/ui's HighlighterFn takes `string`; Shiki's
  // `highlightToHtml` takes a narrower union. Both accept any
  // string Shiki recognises; an unknown language is the highlighter's
  // problem, not ours.
  return highlightToHtml(code, lang as Parameters<typeof highlightToHtml>[1]);
});

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found in index.html');

// StrictMode is intentionally omitted — in dev it causes the root
// React tree (App → AuthProvider → RouterProvider) to mount twice,
// which interacted badly with the AuthProvider's [] mount effect
// (the /me probe ran twice, the second one overwriting any in-
// flight login state). Production builds don't need the dev-only
// safety net, and the OSD app is small enough that the strict-
// mode catch checks aren't buying us much.
//
// If a regression in dev is needed, wrap <App /> in <StrictMode>
// here — just be ready to also handle the doubled mount in
// AuthProvider (e.g. by guarding the probe effect with a ref or
// moving it to a one-shot imperative call).
createRoot(rootEl).render(<App />);
